import { createReducer, on } from '@ngrx/store';

import {
  PartnerAvailabilityFilter,
  PartnerImportStatusFilter,
  PartnerIntegrationTypeFilter,
  PartnerState,
  PartnerStatusFilter,
} from '@admin/src/core/models/partner-state.interface';

import * as PartnerActions from './partner.actions';

const TEMP_PAGE_SIZE = 50;

const initialState: PartnerState = {
  partnersList: {
    filter: {
      search: '',
      type: PartnerIntegrationTypeFilter.All,
      status: PartnerStatusFilter.All,
      availability: PartnerAvailabilityFilter.All,
      importStatus: PartnerImportStatusFilter.All,
    },
    pageSize: TEMP_PAGE_SIZE,
    continuationToken: '',
    data: [],
    loading: false,
  },
  currentPartner: {
    data: null,
    loading: false,
  },
  locationsList: {
    filter: {
      search: '',
      type: PartnerIntegrationTypeFilter.All,
      status: PartnerStatusFilter.All,
      availability: PartnerAvailabilityFilter.All,
      importStatus: PartnerImportStatusFilter.All,
    },
    pageSize: TEMP_PAGE_SIZE,
    continuationToken: '',
    data: [],
    loading: false,
  },
  currentLocation: {
    data: null,
    loading: false,
  },
  doctorsList: {
    filter: {
      search: '',
      type: PartnerIntegrationTypeFilter.All,
      status: PartnerStatusFilter.All,
      availability: PartnerAvailabilityFilter.All,
      importStatus: PartnerImportStatusFilter.All,
    },
    pageSize: TEMP_PAGE_SIZE,
    continuationToken: '',
    data: [],
    loading: false,
  },
  currentDoctor: {
    data: null,
    loading: false,
  },
  offeringsList: {
    filter: {
      search: '',
      type: PartnerIntegrationTypeFilter.All,
      status: PartnerStatusFilter.All,
      availability: PartnerAvailabilityFilter.All,
      importStatus: PartnerImportStatusFilter.All,
      mappedOnly: false,
    },
    pageSize: TEMP_PAGE_SIZE,
    continuationToken: '',
    data: [],
    loading: false,
  },
  currentOffering: {
    data: null,
    loading: false,
  },
};

export const partnerReducer = createReducer(
  initialState,

  // Set Filters
  on(PartnerActions.setPartnersFilter, (state, { filter }) => ({
    ...state,
    partnersList: { ...state.partnersList, filter: filter },
  })),

  // Get Partners List
  on(PartnerActions.getPartnersList, state => ({
    ...state,
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.getPartnersListSuccess, (state, { response }) => ({
    ...state,
    partnersList: {
      ...state.partnersList,
      continuationToken: response.continuationToken || '',
      data: [...state.partnersList.data, ...response.items],
      loading: false,
    },
  })),
  on(PartnerActions.getPartnersListFailure, state => ({
    ...state,
    partnersList: { ...initialState.partnersList, loading: false },
  })),

  // Get Partner By id
  on(PartnerActions.getPartnerById, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
  })),
  on(PartnerActions.getPartnerByIdSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
  })),
  on(PartnerActions.getPartnerByIdFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
  })),

  // Create Partner
  on(PartnerActions.createPartner, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.createPartnerSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: { ...state.partnersList, data: [partner, ...state.partnersList.data], loading: false },
  })),
  on(PartnerActions.createPartnerFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Edit Partner
  on(PartnerActions.editPartner, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.editPartnerSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
      loading: false,
    },
  })),
  on(PartnerActions.editPartnerFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Activate Partner
  on(PartnerActions.activatePartner, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.activatePartnerSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
      loading: false,
    },
  })),
  on(PartnerActions.activatePartnerFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Deactivate Partner
  on(PartnerActions.deactivatePartner, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.deactivatePartnerSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
      loading: false,
    },
  })),
  on(PartnerActions.deactivatePartnerFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Set Partner Available
  on(PartnerActions.setPartnerAvailable, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.setPartnerAvailableSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
      loading: false,
    },
  })),
  on(PartnerActions.setPartnerAvailableFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Set Partner Unavailable
  on(PartnerActions.setPartnerUnavailable, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.setPartnerUnavailableSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
      loading: false,
    },
  })),
  on(PartnerActions.setPartnerUnavailableFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Delete Partner
  on(PartnerActions.deletePartner, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
    partnersList: { ...state.partnersList, loading: true },
  })),
  on(PartnerActions.deletePartnerSuccess, (state, { id }) => ({
    ...state,
    currentPartner: { data: null, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.filter(p => p.id !== id),
      loading: false,
    },
  })),
  on(PartnerActions.deletePartnerFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
    partnersList: { ...state.partnersList, loading: false },
  })),

  // Upload Partner Logo
  on(PartnerActions.uploadPartnerLogo, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: true },
  })),
  on(PartnerActions.uploadPartnerLogoSuccess, (state, { partner }) => ({
    ...state,
    currentPartner: { data: partner, loading: false },
    partnersList: {
      ...state.partnersList,
      data: state.partnersList.data.map(p => (p.id === partner.id ? partner : p)),
    },
  })),
  on(PartnerActions.uploadPartnerLogoFailure, state => ({
    ...state,
    currentPartner: { ...state.currentPartner, loading: false },
  })),

  // Clear State
  on(PartnerActions.clearPartnersList, state => ({
    ...state,
    partnersList: { ...initialState.partnersList },
  })),
  on(PartnerActions.clearCurrentPartner, state => ({
    ...state,
    currentPartner: { data: null, loading: false },
  })),

  // ===== LOCATION REDUCERS =====

  // Set Locations Filter
  on(PartnerActions.setLocationsFilter, (state, { filter }) => ({
    ...state,
    locationsList: { ...state.locationsList, filter: filter },
  })),

  // Get Locations List
  on(PartnerActions.getLocationsList, state => ({
    ...state,
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.getLocationsListSuccess, (state, { response }) => ({
    ...state,
    locationsList: {
      ...state.locationsList,
      continuationToken: response.continuationToken || '',
      data: [...state.locationsList.data, ...response.items],
      loading: false,
    },
  })),
  on(PartnerActions.getLocationsListFailure, state => ({
    ...state,
    locationsList: { ...initialState.locationsList, loading: false },
  })),

  // Get Location By id
  on(PartnerActions.getLocationById, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
  })),
  on(PartnerActions.getLocationByIdSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
  })),
  on(PartnerActions.getLocationByIdFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
  })),

  // Create Location
  on(PartnerActions.createLocation, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.createLocationSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: { ...state.locationsList, data: [location, ...state.locationsList.data], loading: false },
  })),
  on(PartnerActions.createLocationFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Edit Location
  on(PartnerActions.editLocation, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.editLocationSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
      loading: false,
    },
  })),
  on(PartnerActions.editLocationFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Activate Location
  on(PartnerActions.activateLocation, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.activateLocationSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
      loading: false,
    },
  })),
  on(PartnerActions.activateLocationFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Deactivate Location
  on(PartnerActions.deactivateLocation, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.deactivateLocationSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
      loading: false,
    },
  })),
  on(PartnerActions.deactivateLocationFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Set Location Available
  on(PartnerActions.setLocationAvailable, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.setLocationAvailableSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
      loading: false,
    },
  })),
  on(PartnerActions.setLocationAvailableFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Set Location Unavailable
  on(PartnerActions.setLocationUnavailable, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.setLocationUnavailableSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
      loading: false,
    },
  })),
  on(PartnerActions.setLocationUnavailableFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Delete Location
  on(PartnerActions.deleteLocation, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
    locationsList: { ...state.locationsList, loading: true },
  })),
  on(PartnerActions.deleteLocationSuccess, (state, { id }) => ({
    ...state,
    currentLocation: { data: null, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.filter(l => l.id !== id),
      loading: false,
    },
  })),
  on(PartnerActions.deleteLocationFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
    locationsList: { ...state.locationsList, loading: false },
  })),

  // Upload Location Logo
  on(PartnerActions.uploadLocationLogo, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: true },
  })),
  on(PartnerActions.uploadLocationLogoSuccess, (state, { location }) => ({
    ...state,
    currentLocation: { data: location, loading: false },
    locationsList: {
      ...state.locationsList,
      data: state.locationsList.data.map(l => (l.id === location.id ? location : l)),
    },
  })),
  on(PartnerActions.uploadLocationLogoFailure, state => ({
    ...state,
    currentLocation: { ...state.currentLocation, loading: false },
  })),

  // Clear Location State
  on(PartnerActions.clearLocationsList, state => ({
    ...state,
    locationsList: { ...initialState.locationsList },
  })),
  on(PartnerActions.clearCurrentLocation, state => ({
    ...state,
    currentLocation: { data: null, loading: false },
  })),

  // ===== DOCTOR REDUCERS =====

  // Set Doctors Filter
  on(PartnerActions.setDoctorsFilter, (state, { filter }) => ({
    ...state,
    doctorsList: { ...state.doctorsList, filter: filter },
  })),

  // Get Doctors List
  on(PartnerActions.getDoctorsList, state => ({
    ...state,
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.getDoctorsListSuccess, (state, { response }) => ({
    ...state,
    doctorsList: {
      ...state.doctorsList,
      continuationToken: response.continuationToken || '',
      data: [...state.doctorsList.data, ...response.items],
      loading: false,
    },
  })),
  on(PartnerActions.getDoctorsListFailure, state => ({
    ...state,
    doctorsList: { ...initialState.doctorsList, loading: false },
  })),

  // Get Doctor By id
  on(PartnerActions.getDoctorById, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
  })),
  on(PartnerActions.getDoctorByIdSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
  })),
  on(PartnerActions.getDoctorByIdFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
  })),

  // Create Doctor
  on(PartnerActions.createDoctor, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.createDoctorSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: { ...state.doctorsList, data: [doctor, ...state.doctorsList.data], loading: false },
  })),
  on(PartnerActions.createDoctorFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Edit Doctor
  on(PartnerActions.editDoctor, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.editDoctorSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.map(d => (d.id === doctor.id ? doctor : d)),
      loading: false,
    },
  })),
  on(PartnerActions.editDoctorFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Activate Doctor
  on(PartnerActions.activateDoctor, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.activateDoctorSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.map(d => (d.id === doctor.id ? doctor : d)),
      loading: false,
    },
  })),
  on(PartnerActions.activateDoctorFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Deactivate Doctor
  on(PartnerActions.deactivateDoctor, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.deactivateDoctorSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.map(d => (d.id === doctor.id ? doctor : d)),
      loading: false,
    },
  })),
  on(PartnerActions.deactivateDoctorFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Set Doctor Available
  on(PartnerActions.setDoctorAvailable, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.setDoctorAvailableSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.map(d => (d.id === doctor.id ? doctor : d)),
      loading: false,
    },
  })),
  on(PartnerActions.setDoctorAvailableFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Set Doctor Unavailable
  on(PartnerActions.setDoctorUnavailable, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.setDoctorUnavailableSuccess, (state, { doctor }) => ({
    ...state,
    currentDoctor: { data: doctor, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.map(d => (d.id === doctor.id ? doctor : d)),
      loading: false,
    },
  })),
  on(PartnerActions.setDoctorUnavailableFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Delete Doctor
  on(PartnerActions.deleteDoctor, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: true },
    doctorsList: { ...state.doctorsList, loading: true },
  })),
  on(PartnerActions.deleteDoctorSuccess, (state, { id }) => ({
    ...state,
    currentDoctor: { data: null, loading: false },
    doctorsList: {
      ...state.doctorsList,
      data: state.doctorsList.data.filter(d => d.id !== id),
      loading: false,
    },
  })),
  on(PartnerActions.deleteDoctorFailure, state => ({
    ...state,
    currentDoctor: { ...state.currentDoctor, loading: false },
    doctorsList: { ...state.doctorsList, loading: false },
  })),

  // Clear Doctor State
  on(PartnerActions.clearDoctorsList, state => ({
    ...state,
    doctorsList: { ...initialState.doctorsList },
  })),
  on(PartnerActions.clearCurrentDoctor, state => ({
    ...state,
    currentDoctor: { data: null, loading: false },
  })),

  // ===== OFFERING REDUCERS =====

  // Set Offerings Filter
  on(PartnerActions.setOfferingsFilter, (state, { filter }) => ({
    ...state,
    offeringsList: { ...state.offeringsList, filter: filter },
  })),

  // Get Offerings List
  on(PartnerActions.getOfferingsList, state => ({
    ...state,
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.getOfferingsListSuccess, (state, { response }) => ({
    ...state,
    offeringsList: {
      ...state.offeringsList,
      continuationToken: response.continuationToken || '',
      data: [...state.offeringsList.data, ...response.items],
      loading: false,
    },
  })),
  on(PartnerActions.getOfferingsListFailure, state => ({
    ...state,
    offeringsList: { ...initialState.offeringsList, loading: false },
  })),

  // Get Offering By id
  on(PartnerActions.getOfferingById, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
  })),
  on(PartnerActions.getOfferingByIdSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
  })),
  on(PartnerActions.getOfferingByIdFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
  })),

  // Create Offering
  on(PartnerActions.createOffering, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.createOfferingSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: { ...state.offeringsList, data: [offering, ...state.offeringsList.data], loading: false },
  })),
  on(PartnerActions.createOfferingFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Edit Offering
  on(PartnerActions.editOffering, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.editOfferingSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.map(o => (o.id === offering.id ? offering : o)),
      loading: false,
    },
  })),
  on(PartnerActions.editOfferingFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Activate Offering
  on(PartnerActions.activateOffering, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.activateOfferingSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.map(o => (o.id === offering.id ? offering : o)),
      loading: false,
    },
  })),
  on(PartnerActions.activateOfferingFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Deactivate Offering
  on(PartnerActions.deactivateOffering, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.deactivateOfferingSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.map(o => (o.id === offering.id ? offering : o)),
      loading: false,
    },
  })),
  on(PartnerActions.deactivateOfferingFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Set Offering Available
  on(PartnerActions.setOfferingAvailable, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.setOfferingAvailableSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.map(o => (o.id === offering.id ? offering : o)),
      loading: false,
    },
  })),
  on(PartnerActions.setOfferingAvailableFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Set Offering Unavailable
  on(PartnerActions.setOfferingUnavailable, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.setOfferingUnavailableSuccess, (state, { offering }) => ({
    ...state,
    currentOffering: { data: offering, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.map(o => (o.id === offering.id ? offering : o)),
      loading: false,
    },
  })),
  on(PartnerActions.setOfferingUnavailableFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Delete Offering
  on(PartnerActions.deleteOffering, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: true },
    offeringsList: { ...state.offeringsList, loading: true },
  })),
  on(PartnerActions.deleteOfferingSuccess, (state, { id }) => ({
    ...state,
    currentOffering: { data: null, loading: false },
    offeringsList: {
      ...state.offeringsList,
      data: state.offeringsList.data.filter(o => o.id !== id),
      loading: false,
    },
  })),
  on(PartnerActions.deleteOfferingFailure, state => ({
    ...state,
    currentOffering: { ...state.currentOffering, loading: false },
    offeringsList: { ...state.offeringsList, loading: false },
  })),

  // Clear Offering State
  on(PartnerActions.clearOfferingsList, state => ({
    ...state,
    offeringsList: { ...initialState.offeringsList },
  })),
  on(PartnerActions.clearCurrentOffering, state => ({
    ...state,
    currentOffering: { data: null, loading: false },
  }))
);
